import { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import {
	FlyoutMenuItem,
	FlyoutMenuItemContent,
	FlyoutMenuItemTrigger,
} from '@atlaskit/side-nav-items/flyout-menu-item';

import { useFastshiftSelector } from '@/state';

import { messages } from './messages';
import { OrganizationsFlyoutContent } from './organizations-flyout-content';

import type { Organization } from './types';

export type OrganizationsFlyoutMenuItemProps = {
	onOrganizationSelect?: (organization: Organization) => void;
};

export const OrganizationsFlyoutMenuItem = ({
	onOrganizationSelect,
}: OrganizationsFlyoutMenuItemProps) => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);

	const { data, error, pending } = useFastshiftSelector((state) => state.organizations);

	const handleOpenChange = useCallback((nextIsOpen: boolean) => {
		setIsOpen(nextIsOpen);
	}, []);

	const handleOrganizationSelect = useCallback(
		(organization: Organization) => {
			setIsOpen(false);
			onOrganizationSelect?.(organization);
		},
		[onOrganizationSelect],
	);

	return (
		<FlyoutMenuItem isOpen={isOpen} onOpenChange={handleOpenChange}>
			<FlyoutMenuItemTrigger
				testId="side-nav-organizations"
				elemBefore={<PeopleGroupIcon label="" />}
				onClick={() => handleOpenChange(!isOpen)}
			>
				{formatMessage(messages.organizationsNavLabel)}
			</FlyoutMenuItemTrigger>
			<FlyoutMenuItemContent
				containerTestId="organizations-flyout"
				onClose={() => setIsOpen(false)}
			>
				<OrganizationsFlyoutContent
					error={error}
					organizations={data}
					pending={pending}
					onOrganizationSelect={handleOrganizationSelect}
				/>
			</FlyoutMenuItemContent>
		</FlyoutMenuItem>
	);
};
